/**
 * WonPrompt — non-blocking dialog that fires after a lead or proposal
 * advances to a "won" state. Asks the user whether to immediately create
 * the next workflow record (Proposal for Lead-Won, Contract for Proposal-Won)
 * or skip and revisit later.
 *
 * Usage:
 *   <WonPrompt
 *     open={open}
 *     onOpenChange={setOpen}
 *     title="Lead won — create a proposal?"
 *     description="..."
 *     primaryLabel="Create proposal"
 *     onConfirm={() => createProposalFromLead()}
 *   />
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2 } from 'lucide-react';

export function WonPrompt({ open, onOpenChange, title, description, primaryLabel, onConfirm, testId = 'won-prompt' }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={testId}>
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="h-7 w-7 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center">
              <CheckCircle2 className="h-4 w-4" />
            </div>
            <DialogTitle className="text-base">{title}</DialogTitle>
          </div>
          <DialogDescription className="text-sm text-muted-foreground">{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid={`${testId}-skip`}
          >
            Skip for now
          </Button>
          <Button
            onClick={() => { onConfirm(); onOpenChange(false); }}
            data-testid={`${testId}-confirm`}
          >
            {primaryLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
